import type { SettingLookFamily } from '@gk/contracts'
import { addDays } from '@gk/contracts'
import { dayOfMonth, weekdayShort } from '../../data/planUi.ts'
import type { History } from '../../data/settingsHistory.ts'
import { defaultCycleLength, type Cut, type Reason, type RuleSetting, type SetAside, type SettingsWarning } from '../../data/settingsRule.ts'

/** M25-A (docs/M25.md D, rule 1): THE WORDS OF THE SETTINGS ASSISTANT, pure. Every text is a key of `plan.settingsRule.*` (fr and en together, merged from
 *  `packages/i18n/parts/m25-a.json`); the rule's printed steps are `RULE_STEPS` in `data/settingsRule.ts`. A planned cut is always said to be planned and a
 *  finished one that no confirmation backs is said to be « d'après le prévu »: these texts never present planned work as done. */
export type T = (k: string, o?: Record<string, unknown>) => string

/** « 28/10 » from a site-local ISO day: text only, no date object, no clock. */
export const dm = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

/** « fait le 28/10 », « fait le 28/10, d'après le prévu », « prévu le 30/10 », with « sur 4 zones » when the areas are asked for. */
export function cutText(t: T, cut: Cut, withAreas = false): string {
  const day = dm(cut.day)
  const base = cut.kind === 'planned' ? t('plan.settingsRule.cut.planned', { day }) : cut.basis === 'confirmed' ? t('plan.settingsRule.cut.done', { day }) : t('plan.settingsRule.cut.doneAsPlanned', { day })
  return withAreas ? base + t('plan.settingsRule.cut.onAreas', { count: cut.areas }) : base
}

/** Why a choice is proposed: the step that decided and what it saw. */
export function reasonText(t: T, r: Reason): string {
  const cut = r.prev ? cutText(t, r.prev) : ''
  switch (r.step) {
    case 'longest': return r.last ? t('plan.settingsRule.why.longest', { cut: cutText(t, r.last) }) : t('plan.settingsRule.why.never')
    case 'fewest': return t('plan.settingsRule.why.fewest', { n: r.uses })
    case 'notLast': return r.prev ? t('plan.settingsRule.why.notLast', { cut }) : t('plan.settingsRule.why.only')
    case 'clockwise': return t('plan.settingsRule.why.clockwise', { cut })
    case 'otherWay': return t('plan.settingsRule.why.otherWay', { cut })
    case 'noLapLimit': return t('plan.settingsRule.why.noLapLimit', { n: r.count ?? 0, limit: r.limit ?? 0 })
    case 'lowerDue': return t('plan.settingsRule.why.lowerDue', { n: r.count ?? 0, limit: r.limit ?? 0 })
    case 'toolPrevLowered': return t('plan.settingsRule.why.toolPrevLowered', { cut })
    case 'toolLimit': return t('plan.settingsRule.why.toolLimit', { n: r.count ?? 0, limit: r.limit ?? 0 })
    default: return t(`plan.settingsRule.why.${r.step}`)
  }
}

/** Why a choice is set aside: « écarté : même axe qu'au dernier passage, fait le 28/10 sur 4 zones ». */
export function asideText(t: T, a: SetAside, family: SettingLookFamily): string {
  switch (a.step) {
    case 'repeat': return t(family === 'pat' ? 'plan.settingsRule.aside.repeatPat' : 'plan.settingsRule.aside.repeatDir', { cut: a.cut ? cutText(t, a.cut, true) : '' })
    case 'noLapNotReached': return t('plan.settingsRule.aside.noLapNotReached', { n: a.count ?? 0, limit: a.limit ?? 0 })
    default: return t(`plan.settingsRule.aside.${a.step}`)
  }
}

/** The history line, in two parts that are each said, with « l'ordre de vos choix » only when both are zero; then what was ignored. */
export function readText(t: T, h: Pick<History, 'counts'>, lastPlannedDay: string): string {
  const c = h.counts
  const done = c.done > 0
    ? t('plan.settingsRule.read.done', { count: c.done, since: dm(c.firstDone ?? ''), confirmed: c.confirmed })
    : t('plan.settingsRule.read.doneNone')
  const planned = c.planned > 0 ? t('plan.settingsRule.read.planned', { count: c.planned, until: dm(lastPlannedDay) }) : t('plan.settingsRule.read.plannedNone', { until: dm(lastPlannedDay) })
  const end = c.done === 0 && c.planned === 0 ? t('plan.settingsRule.read.endOrder') : t('plan.settingsRule.read.end')
  const ignored = c.ignored > 0 ? ' ' + t('plan.settingsRule.read.ignored', { count: c.ignored }) : ''
  return `${done}${planned}${end}${ignored}`
}
/** The last day the planned work is read up to: the day before the target. */
export const plannedUntil = (target: string): string => addDays(target, -1)

/** « jeu. 30 » */
export const dayLabel = (iso: string, lang: string): string => `${weekdayShort(iso, lang)} ${dayOfMonth(iso)}`

/** A warning as a row: « jeu. 30 : même axe qu'au dernier passage (fait le mar. 28) ». */
export function warningText(t: T, w: SettingsWarning, lang: string): string {
  const first = w.areas[0]!
  const prev = first.prevKind === 'planned'
    ? t('plan.settingsRule.warn.prevPlanned', { day: dayLabel(first.prevDay, lang) })
    : first.prevBasis === 'confirmed' ? t('plan.settingsRule.warn.prevDone', { day: dayLabel(first.prevDay, lang) }) : t('plan.settingsRule.warn.prevDoneAsPlanned', { day: dayLabel(first.prevDay, lang) })
  return t(w.family === 'pat' ? 'plan.settingsRule.warn.pat' : 'plan.settingsRule.warn.dir', { day: dayLabel(w.day, lang), prev, n: w.areas.length })
}

/** A cycle's default length and why: « 8 étapes : chaque … une fois », « 12 étapes : toutes les combinaisons n'y tiennent pas ». */
export function cycleWhy(t: T, settings: readonly RuleSetting[], leave: ReadonlySet<string>): string {
  const len = defaultCycleLength(settings, leave)
  if (len.capped) return t('plan.settingsRule.cycle.whyCapped', { count: len.n, combos: len.combos })
  if (settings.some((s) => s.family === 'lap') && len.senses > 1) return t('plan.settingsRule.cycle.whyPairs', { count: len.n })
  return t('plan.settingsRule.cycle.whyOnce', { count: len.n })
}
